/**
 * TG-B — Transaction history. Lists past (stopped) transactions for the
 * station; selecting one shows its persisted progress series (power · energy ·
 * SoC) via the shared TxSeriesChart.
 */
import { useEffect } from 'react';
import { useStationStore } from '@/state/useStationStore';
import { useTransactionHistoryStore } from '@/state/useTransactionHistoryStore';
import { TxSeriesChart } from '@/ui/components/TxSeriesChart';
import { formatDateTime, pluralize } from '@/lib/format';

function durationLabel(startedAt: number, stoppedAt: number | undefined): string {
  if (stoppedAt === undefined) return '—';
  const s = Math.max(0, Math.round((stoppedAt - startedAt) / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

export function HistoryTab(): JSX.Element {
  const stationId = useStationStore((s) => s.stationId);
  const transactions = useTransactionHistoryStore((s) => s.transactions);
  const status = useTransactionHistoryStore((s) => s.status);
  const selectedTxId = useTransactionHistoryStore((s) => s.selectedTxId);
  const selectedSamples = useTransactionHistoryStore((s) => s.selectedSamples);
  const load = useTransactionHistoryStore((s) => s.load);
  const select = useTransactionHistoryStore((s) => s.select);

  useEffect(() => {
    void load(stationId);
  }, [stationId, load]);

  return (
    <div
      role="tabpanel"
      id="tab-panel-history"
      aria-labelledby="tab-history"
      className="space-y-4 p-4"
      data-testid="history-tab"
    >
      <section className="rounded border border-slate-700 bg-slate-900/60 p-3">
        <header className="mb-2 flex items-baseline justify-between text-sm">
          <h2 className="font-medium text-slate-100">Past transactions</h2>
          <span className="text-[10px] text-slate-500" data-testid="history-count">
            {pluralize(transactions.length, 'transaction')}
          </span>
        </header>
        {status === 'ready' && transactions.length === 0 ? (
          <p className="py-6 text-center text-xs text-slate-500">
            No completed transactions yet. Start and stop a transaction to see it here.
          </p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[44rem] text-left text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">Tx</th>
                <th className="px-2 py-1">Conn</th>
                <th className="px-2 py-1">idTag</th>
                <th className="px-2 py-1">Started</th>
                <th className="px-2 py-1">Duration</th>
                <th className="px-2 py-1">Energy</th>
                <th className="px-2 py-1">Stop</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => {
                const energyKwh =
                  t.stoppedMeterWh !== undefined
                    ? ((t.stoppedMeterWh - t.startedMeterWh) / 1000).toFixed(3)
                    : '—';
                const id = Number(t.id);
                const isSel = selectedTxId === id;
                return (
                  <tr
                    key={id}
                    className={`border-t border-slate-800 ${isSel ? 'bg-slate-800/60' : ''}`}
                    data-testid={`history-row-${id}`}
                  >
                    <td className="px-2 py-1 font-mono text-slate-200">#{id}</td>
                    <td className="px-2 py-1 text-slate-300">{String(t.connectorId)}</td>
                    <td className="px-2 py-1 font-mono text-slate-300">{t.idTag}</td>
                    <td className="px-2 py-1 text-slate-400">{formatDateTime(t.startedAt)}</td>
                    <td className="px-2 py-1 text-slate-300">
                      {durationLabel(t.startedAt, t.stoppedAt)}
                    </td>
                    <td className="px-2 py-1 text-slate-300">{energyKwh} kWh</td>
                    <td className="px-2 py-1 text-slate-400">{t.stopReason ?? '—'}</td>
                    <td className="px-2 py-1 text-right">
                      <button
                        type="button"
                        onClick={() => void select(stationId, id)}
                        className="rounded bg-slate-700 px-2 py-0.5 text-[10px] text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300"
                        data-testid={`history-view-${id}`}
                      >
                        View graph
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {selectedTxId !== null ? (
        selectedSamples && selectedSamples.length > 0 ? (
          <TxSeriesChart samples={selectedSamples} note={`tx #${selectedTxId}`} testId="history-chart" />
        ) : (
          <section
            className="rounded border border-slate-700 bg-slate-900/60 p-4 text-xs text-slate-500"
            data-testid="history-chart-empty"
          >
            No progress series was recorded for tx #{selectedTxId} (it predates progress capture,
            or charged with no power flow).
          </section>
        )
      ) : null}
    </div>
  );
}
